<template>
  <div id="NavBar">
    <b-navbar fixed-top :shadow="true">
      <template slot="brand">
        <b-navbar-item>
          <img
            class="nav-logo"
            alt="dtac at home logo"
            src="../assets/dah-logo.svg"
            @click.prevent="navigateToPageName('home')"
          />
        </b-navbar-item>
      </template>

      <template slot="end">
        <!-- Home -->
        <b-navbar-item
          tag="router-link"
          :to="{ name: 'home' }"
          :class="{ 'is-active': this.$route.name === 'home' }"
          >{{ $t("navbar.home") }}</b-navbar-item
        >

        <!-- Customer Registration -->
        <b-navbar-item
          v-if="isRegister"
          tag="router-link"
          :to="{ name: 'register' }"
          :class="{ 'is-active': this.$route.name === 'register' }"
          >{{ $t("navbar.register") }}</b-navbar-item
        >

        <!-- Customers Management -->
        <b-navbar-item
          v-if="isCustomerManage"
          tag="router-link"
          :to="{ name: 'customers' }"
          :class="{ 'is-active': this.$route.name === 'customers' }"
          >{{ $t("navbar.customers") }}</b-navbar-item
        >

        <!-- Prospect Customers Management -->
        <b-navbar-item
          v-if="isProspectManage"
          tag="router-link"
          :to="{ name: 'prospects' }"
          :class="{ 'is-active': this.$route.name === 'prospects' }"
          >{{ $t("navbar.prospects") }}</b-navbar-item
        >

        <!-- Admin Users Management -->
        <b-navbar-item
          v-if="isAdminUserManage"
          tag="router-link"
          :to="{ name: 'users' }"
          :class="{ 'is-active': this.$route.name === 'users' }"
          >{{ $t("navbar.admins") }}</b-navbar-item
        >

        <!-- Device Management -->
        <b-navbar-item
          v-if="isDeviceManage"
          tag="router-link"
          :to="{ name: 'devices' }"
          :class="{ 'is-active': this.$route.name === 'devices' }"
          >{{ $t("navbar.devices") }}</b-navbar-item
        >

        <b-dropdown
          aria-role="menu"
          position="is-bottom-left"
          :mobile-modal="true"
        >
          <a class="navbar-item" slot="trigger" role="button">
            <span>{{ $t("navbar.profile") }}</span>
            <b-icon icon="menu-down" />
          </a>
          <!-- Sub-Menu: Welcome Message  -->
          <b-dropdown-item v-if="userProfile.name" aria-role="menuitem" custom>
            {{ $t("navbar.dropdown.welcome") }}
            <b v-if="userProfile.name">{{ userProfile.name }}</b>
            <b v-else>John Doe</b>
          </b-dropdown-item>
          <hr class="dropdown-divider" />
          <!-- Sub-Menu: MyProfile  -->
          <b-dropdown-item
            value="myprofile"
            aria-role="menuitem"
            @click="navigateToPageName('myprofile')"
          >
            <b-icon icon="settings" />
            <span />
            {{ $t("navbar.dropdown.setting") }}
          </b-dropdown-item>
          <!-- Sub-Menu: Logout  -->
          <b-dropdown-item
            value="logout"
            aria-role="menuitem"
            @click="performSignOut()"
            :disabled="isDisableLogout"
          >
            <b-icon icon="logout" />
            <span />
            {{ $t("navbar.dropdown.logout") }}
          </b-dropdown-item>
          <hr class="navbar-divider" />
          <!-- Sub-Menu: Version Number  -->
          <b-dropdown-item
            aria-role="menuitem"
            style="text-align: left;"
            @click="copyTokenToClipboard()"
            >{{ $t("navbar.dropdown.version") }} {{ version }}</b-dropdown-item
          >
        </b-dropdown>

        <!-- Language -->
        <b-navbar-item>
          <div @click.prevent="changeLanguage()">
            <div v-if="$i18n.locale === 'en'" style="padding-top: 5px;">
              <img
                src="../assets/flag-th.svg"
                alt="thai language indicator"
                style="width: 32px;"
              />
            </div>
            <div v-else-if="$i18n.locale === 'th'" style="padding-top: 5px;">
              <img
                src="../assets/flag-en.svg"
                alt="english language indicator"
                style="width: 32px;"
              />
            </div>
            <div v-else>N/A</div>
          </div>
        </b-navbar-item>
      </template>
    </b-navbar>
    <router-view @ws_update="websocketUpdate" />
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import AlertManager from "@/managers/AlertManager";
import ConfigManager from "@/managers/ConfigManager";
import TokenManager from "@/managers/TokenManager";

import UserApiManager from "@/apis/UserApiManager";

export default {
  name: "NavBar",
  data() {
    return {
      version: null,
      socket: null,
      isRegister: false,
      isPlanning: false,
      isDeviceManage: false,
      isCustomerManage: false,
      isProspectManage: false,
      isAdminUserManage: false,
      isDisableLogout: false
    };
  },
  computed: mapState(["userProfile"]),
  watch: {
    /*
      Watch any update event of user profile 
    */
    userProfile: {
      handler: function(newProfile) {
        this.setUpMenuVisibility(newProfile.roles);
      },
      deep: true
    }
  },
  methods: {
    ...mapMutations({
      setUserProfile: "SET_USER_PROFILE",
      wsConnected: "SOCKET_CONNECT",
      wsDisconnected: "SOCKET_DISCONNECT",
      wsErrorRecieved: "SOCKET_ERROR",
      wsMessageRecieved: "SOCKET_MESSAGE"
    }),
    updateNavbarUi: function(payload) {
      this.$refs.navbar.isActive = payload;
    },
    changeLanguage: function() {
      switch (this.$i18n.locale) {
        case "en":
          this.$i18n.locale = "th";
          break;

        default:
          this.$i18n.locale = "en";
          break;
      }
    },
    navigateToPageName: function(name) {
      switch (name) {
        case "home":
        case "myprofile":
          this.$router.push({ name });
          break;

        default:
          AlertManager.showErrorSnackbar("Wrong Navigation Menu");
          break;
      }
    },
    performSignOut: async function() {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const logoutResult = await UserApiManager.requestLogout();
      loadingComponent.close();

      switch (logoutResult.code) {
        case "0":
          this.$router.push({ name: "login" });
          break;

        default:
          AlertManager.showErrorSnackbar(logoutResult.message);
          break;
      }
    },
    getCurrentUserProfile: async function() {
      const loadingComponent = this.$buefy.loading.open({
        container: null
      });
      const loggedInUser = await UserApiManager.requestGetCurrentUser();
      loadingComponent.close();

      this.setUserProfile(loggedInUser);
    },
    setUpMenuVisibility: function(userRoles) {
      this.isRegister = userRoles.customer_register;
      this.isPlanning = userRoles.map_area;
      this.isCustomerManage = userRoles.customer_list;
      this.isAdminUserManage = userRoles.user_manage;
      this.isProspectManage = userRoles.customer_propect;
      this.isDeviceManage = userRoles.device_list;
    },
    setUpWebSocketEnvironment: async function() {
      const wsTokenResponse = await UserApiManager.requestWebSocketAuth();
      switch (wsTokenResponse.code) {
        case "0":
          // eslint-disable-next-line
          let wsURL = ConfigManager.getWebSocketURL();
          this.socket = new WebSocket(wsURL, wsTokenResponse.data.token);
          this.socket.onopen = () => {
            this.wsConnected();
          };

          this.socket.onmessage = e => {
            const message = JSON.parse(e.data);
            this.wsMessageRecieved(message);
          };

          this.socket.onerror = error => {
            this.wsErrorRecieved(error);
          };

          this.socket.onclose = () => {
            this.wsDisconnected();
          };
          break;

        default:
          AlertManager.showErrorSnackbar(wsTokenResponse.message);
          break;
      }
    },
    websocketUpdate(value) {
      let interval = setInterval(() => {
        if (this.socket) {
          this.socket.send(value);
          clearInterval(interval);
        }
      }, 1000);
    },
    copyTokenToClipboard: async function() {
      if (this.isAdminUserManage) {
        try {
          const textToCopy = TokenManager.getAdfsToken();
          await navigator.clipboard.writeText(textToCopy);
          AlertManager.showSuccessSnackbar("The text token was copied!");
        } catch (err) {
          AlertManager.showErrorSnackbar(err);
        }
      }
    }
  },
  mounted() {
    this.version = ConfigManager.getVersion();
    this.getCurrentUserProfile();
    this.setUpWebSocketEnvironment();
    this.isDisableLogout = TokenManager.getEucToken();
  }
};
</script>

<style>
.nav-logo {
  width: 100px;
  max-height: 2.5rem !important;
}
</style>
